"use client";

import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link2, ArrowRight, ArrowLeft, Loader2, DollarSign, LogIn, Shield, CheckCircle2, ExternalLink, Pencil } from "lucide-react";
import { useOnboardingMode } from "@/hooks/use-onboarding-mode";
import { useSession } from "next-auth/react";
import { toast } from "sonner";
import { trackClientEvent } from "@/lib/analyticsClient";
import Link from "next/link";
import { ManualPositionModal } from "@/components/modals/manual-position-modal";
import { IBKRSetupModal } from "@/components/modals/ibkr-setup-modal";

const BROKERS = [
  { value: "SCHWAB", label: "Schwab", logo: "https://cdn.brandfetch.io/schwab.com/w/400/h/400" },
  { value: "E*TRADE", label: "E*Trade", logo: "https://cdn.brandfetch.io/etrade.com/w/400/h/400" },
  { value: "WEBULL_US", label: "Webull", logo: "https://cdn.brandfetch.io/webull.com/w/400/h/400" },
  { value: "ROBINHOOD", label: "Robinhood", logo: "https://cdn.brandfetch.io/robinhood.com/w/400/h/400" },
  { value: "FIDELITY", label: "Fidelity", logo: "https://cdn.brandfetch.io/fidelity.com/w/400/h/400" },
  { value: "TASTYTRADE", label: "Tastytrade", logo: "https://cdn.brandfetch.io/tastytrade.com/w/400/h/400" },
  { value: "TRADIER", label: "Tradier", logo: "https://cdn.brandfetch.io/tradier.com/w/400/h/400" },
  { value: "MOOMOO", label: "Moomoo", logo: "https://cdn.brandfetch.io/moomoo.com/w/400/h/400" },
  { value: "QUESTRADE", label: "Questrade", logo: "https://cdn.brandfetch.io/questrade.com/w/400/h/400" },
  { value: "INTERACTIVE_BROKERS", label: "IBKR", logo: "https://cdn.brandfetch.io/idcABCQwX-/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1667570681287" },
] as const;

type ConnectState = "select" | "connecting" | "processing" | "done" | "already-connected";

interface ConnectedBrokerInfo {
  name: string;
  cash: number;
}

export function BrokerConnectStep() {
  const { showBrokerConnect, setShowBrokerConnect, setShowPricing, goBackStage, isOnboardingMode } = useOnboardingMode();
  const { data: session } = useSession();
  const isLoggedIn = !!session?.user?.id;
  const [selectedBroker, setSelectedBroker] = useState<string>("");
  const [connectState, setConnectState] = useState<ConnectState>("select");
  const [estimatedIncome, setEstimatedIncome] = useState<number | null>(null);
  const [connectedBrokerInfo, setConnectedBrokerInfo] = useState<ConnectedBrokerInfo | null>(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showIBKRModal, setShowIBKRModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [processingHint, setProcessingHint] = useState<string | null>(null);
  const activationTrackedRef = useRef(false);
  const connectionCheckedRef = useRef(false);
  const normalizeBrokerName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "");
  const connectedBrokerLogo = connectedBrokerInfo
    ? BROKERS.find((broker) => {
      const normalizedLabel = normalizeBrokerName(broker.label);
      const normalizedConnectedName = normalizeBrokerName(connectedBrokerInfo.name);
      return normalizedConnectedName.includes(normalizedLabel) || normalizedLabel.includes(normalizedConnectedName);
    })?.logo
    : null;
  const isIbkrBroker = (brokerSlug: string) => brokerSlug === "INTERACTIVE_BROKERS";

  // Auto-open manual modal if returning from login with manual intent
  useEffect(() => {
    if (!showBrokerConnect || !isLoggedIn) return;
    try {
      const autoManual = localStorage.getItem('qw_onboarding_auto_manual');
      if (autoManual) {
        localStorage.removeItem('qw_onboarding_auto_manual');
        setShowManualModal(true);
      }
    } catch {}
  }, [showBrokerConnect, isLoggedIn]);

  // Check for existing broker connections on mount
  useEffect(() => {
    if (!showBrokerConnect || !isLoggedIn || connectionCheckedRef.current) return;
    connectionCheckedRef.current = true;

    Promise.all([
      fetch("/api/broker/connections").then(r => r.ok ? r.json() : null),
      fetch("/api/positions/snaptrade").then(r => r.ok ? r.json() : null),
    ])
      .then(([connData, posData]) => {
        const auths = connData?.authorizations || [];
        const ibkrConns = connData?.ibkrConnections || [];
        const hasConnection = auths.length > 0 || ibkrConns.length > 0;

        if (hasConnection) {
          const brokerName = auths[0]?.brokerage?.display_name || auths[0]?.brokerage?.name || ibkrConns[0]?.accountId || "Your broker";
          const accounts = posData?.accounts || [];
          const totalCash = accounts.reduce((s: number, a: any) => s + (a.cash || 0), 0);
          setConnectedBrokerInfo({ name: brokerName, cash: totalCash });
          setEstimatedIncome(totalCash > 0 ? Math.round(totalCash * 0.03) : null);
          setConnectState("already-connected");
        }
      })
      .catch(() => {});
  }, [showBrokerConnect, isLoggedIn]);

  // Track activation and auto-connect if returning from login with a saved broker
  const autoConnectTriggered = useRef(false);
  useEffect(() => {
    if (!showBrokerConnect) return;
    if (!activationTrackedRef.current) {
      activationTrackedRef.current = true;
      trackClientEvent({
        name: "activation_screen_viewed",
        params: { path: "pro", first_time_activation_viewed: true },
        userId: session?.user?.id ?? undefined,
      });
    }
    if (autoConnectTriggered.current || !isLoggedIn) return;
    try {
      const savedBroker = localStorage.getItem('qw_onboarding_selected_broker');
      if (savedBroker) {
        localStorage.removeItem('qw_onboarding_selected_broker');
        autoConnectTriggered.current = true;
        setSelectedBroker(savedBroker);
        if (isIbkrBroker(savedBroker)) {
          setShowIBKRModal(true);
          return;
        }
        setTimeout(() => {
          setConnectState("connecting");
          fetch("/api/broker/connect", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ brokerSlug: savedBroker, connectionType: "read" }),
          })
            .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.error || "Connection failed"); }))
            .then(data => {
              if (data.redirectUrl) {
                window.open(data.redirectUrl, "_blank", "width=600,height=700");
                setConnectState("processing");
                pollForAccounts();
              }
            })
            .catch(e => {
              toast.error(e instanceof Error ? e.message : "Failed to connect broker");
              setConnectState("select");
            });
        }, 500);
      }
    } catch {}
  }, [showBrokerConnect, session?.user?.id, isLoggedIn]);

  const handleConnect = async () => {
    if (!selectedBroker) return;

    if (!isLoggedIn) {
      try {
        localStorage.setItem("qw_onboarding_broker_intent", "1");
        localStorage.setItem("qw_onboarding_selected_broker", selectedBroker);
      } catch {}
      window.location.href = "/login";
      return;
    }

    if (isIbkrBroker(selectedBroker)) {
      setShowIBKRModal(true);
      return;
    }

    setConnectState("connecting");

    try {
      const res = await fetch("/api/broker/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brokerSlug: selectedBroker, connectionType: "read" }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Connection failed");
      }

      const data = await res.json();

      if (data.redirectUrl) {
        window.open(data.redirectUrl, "_blank", "width=600,height=700");
        setConnectState("processing");
        pollForAccounts();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to connect broker");
      setConnectState("select");
    }
  };

  const pollForAccounts = async () => {
    let attempts = 0;
    const maxAttempts = 30;
    let syncTriggered = false;

    const check = async () => {
      attempts++;
      try {
        const res = await fetch("/api/positions/snaptrade?refresh=true");
        if (res.ok) {
          const data = await res.json();
          const accounts = data.accounts || [];
          if (accounts.length > 0) {
            // Connection is live: hit /api/broker/connections once so the
            // server auto-enqueues the first journal sync for this broker.
            if (!syncTriggered) {
              syncTriggered = true;
              fetch("/api/broker/connections").catch(() => {});
            }
            const totalCash = accounts.reduce((s: number, a: any) => s + (a.cash || 0), 0);
            setEstimatedIncome(Math.round(totalCash * 0.03));
            setConnectState("done");
            return;
          }
        }
      } catch {}

      // Surface queue-backed sync state while we wait
      try {
        const statusRes = await fetch("/api/broker/sync/status");
        if (statusRes.ok) {
          const status = await statusRes.json();
          if (status.state === "queued") {
            setProcessingHint("Connected! Your account is queued for its first sync...");
          } else if (status.state === "processing") {
            setProcessingHint("Connected! Processing your trade history...");
          }
        }
      } catch {}

      if (attempts < maxAttempts) {
        setTimeout(check, 5000);
      } else {
        setConnectState("done");
        setEstimatedIncome(null);
      }
    };

    setTimeout(check, 5000);
  };

  // Lets the user step back into the tour instead of being forced to connect.
  // The connect modal itself stays non-dismissible (no X / escape / outside click);
  // this is the only sanctioned way back so they never get stuck on this step.
  const handleBackToTour = () => {
    try { localStorage.removeItem("qw_wheel_at_broker_connect"); } catch {}
    setShowBrokerConnect(false);
    setConnectState("select");
    setSelectedBroker("");
    goBackStage();
  };

  const handleConnectLater = () => {
    if (!isLoggedIn) {
      try { localStorage.setItem("qw_onboarding_continue_testing", "1"); } catch {}
      window.location.href = "/login";
      return;
    }
    setShowBrokerConnect(false);
    setConnectState("select");
    setSelectedBroker("");
  };

  const handleContinueToTrial = () => {
    setShowBrokerConnect(false);
    setConnectState("select");
    setSelectedBroker("");
    setShowPricing(true);
  };

  const handleManualComplete = () => {
    setShowManualModal(false);
    setShowBrokerConnect(false);
    setConnectState("select");
    setSelectedBroker("");
    setShowPricing(true);
  };

  return (
    <Dialog open={showBrokerConnect} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg p-0 gap-0 overflow-visible border-0 bg-transparent shadow-none [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Connect your broker</DialogTitle>
        <div className="flex justify-center p-4 max-h-[90vh] overflow-y-auto">
          <div className="w-full max-w-lg rounded-lg overflow-hidden shadow-xl">
            <div className="h-1.5 rounded-t-lg bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
            <div className="relative rounded-b-lg border border-t-0 border-border/50 bg-background shadow-lg">
              {isOnboardingMode && (connectState === "select" || connectState === "already-connected") && (
                <button
                  type="button"
                  onClick={handleBackToTour}
                  className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Back to tour"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>
              )}
              <div className="p-6 sm:p-8 space-y-5">

                {connectState === "already-connected" && connectedBrokerInfo && (
                  <>
                    <div className="relative mx-auto w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                      {connectedBrokerLogo && (
                        <div className="absolute -right-1 -bottom-1 h-6 w-6 rounded-full border border-border/70 bg-background p-0.5 shadow-sm">
                          <img
                            src={connectedBrokerLogo}
                            alt={`${connectedBrokerInfo.name} logo`}
                            className="h-full w-full rounded-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = "none";
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 text-center">
                      <h2 className="text-lg font-semibold tracking-tight">
                        {connectedBrokerInfo.name} is connected
                      </h2>
                      {estimatedIncome !== null && estimatedIncome > 0 ? (
                        <div className="mt-3 rounded-xl bg-gradient-to-r from-emerald-950/85 via-emerald-900/65 to-emerald-950/85 border border-emerald-500/25 p-4">
                          <p className="text-xs text-emerald-300/80 uppercase tracking-wider font-semibold">You&apos;re leaving on the table</p>
                          <p className="text-2xl font-semibold text-emerald-200 tabular-nums mt-1">
                            ~${estimatedIncome.toLocaleString()}<span className="text-sm font-normal text-emerald-300/70">/mo</span>
                          </p>
                          <p className="text-[11px] text-zinc-300/70 mt-1">in options income from idle cash</p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Your portfolio is synced. Start your trial to see personalized income opportunities.
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Button
                        size="lg"
                        className="w-full h-11 bg-gradient-to-r from-lime-400 to-green-400 text-zinc-900 hover:from-lime-500 hover:to-green-500 font-semibold"
                        onClick={handleContinueToTrial}
                      >
                        Start 7 Days for $1
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-muted-foreground"
                        asChild
                      >
                        <Link href="/dashboard/broker-integration" className="inline-flex w-full items-center justify-center">
                          <ExternalLink className="mr-1.5 inline-block h-3.5 w-3.5 shrink-0" />
                          Connect more brokers
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-muted-foreground"
                        onClick={handleConnectLater}
                      >
                        Connect later &amp; continue testing
                      </Button>
                    </div>
                  </>
                )}

                {connectState === "select" && (
                  <>
                    <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                      <Link2 className="h-6 w-6 text-amber-500" />
                    </div>

                    <div className="space-y-2 text-center">
                      <h2 className="text-lg font-semibold tracking-tight">
                        Connect your broker
                      </h2>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        To show how much options income you&apos;re leaving on the table in your real accounts, QuantWheel needs read-only access to your broker.
                      </p>
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {BROKERS.map((b) => (
                        <button
                          key={b.value}
                          type="button"
                          onClick={() => setSelectedBroker(b.value)}
                          className={`flex flex-col items-center gap-1.5 rounded-lg border p-2.5 transition-all hover:bg-muted/50 ${
                            selectedBroker === b.value
                              ? "border-amber-500 ring-1 ring-amber-500/40 bg-amber-500/5"
                              : "border-border/50"
                          }`}
                        >
                          <img
                            src={b.logo}
                            alt=""
                            className="w-7 h-7 rounded object-cover"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = "none";
                            }}
                          />
                          <span className="text-[10px] text-muted-foreground leading-tight text-center">
                            {b.label}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="flex items-start gap-2 text-[11px] text-muted-foreground/70">
                      <Shield className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground/50" />
                      <p>
                        Read-only. No trades are placed without your confirmation. We do not sell your data.{" "}
                        <Link href="/tos" className="underline hover:text-foreground transition-colors">
                          Terms of Service
                        </Link>
                        {" · "}
                        You can disconnect at any time from{" "}
                        <Link href="/dashboard/broker-integration" className="underline hover:text-foreground transition-colors">
                          Broker Connections
                        </Link>.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Button
                        size="lg"
                        className="w-full h-11 bg-foreground text-background hover:bg-foreground/90"
                        disabled={!selectedBroker}
                        onClick={handleConnect}
                      >
                        {isLoggedIn ? (
                          <>
                            Connect
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        ) : (
                          <>
                            <LogIn className="mr-2 h-4 w-4" />
                            Sign in &amp; Connect
                          </>
                        )}
                      </Button>

                      <div className="relative flex items-center gap-3 py-1">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-[11px] text-muted-foreground/60">or</span>
                        <div className="flex-1 h-px bg-border" />
                      </div>

                      {showLoginPrompt && !isLoggedIn ? (
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2.5">
                          <p className="text-xs text-muted-foreground text-center">
                            To save your positions, we&apos;ll need to create an account first.
                          </p>
                          <Button
                            size="sm"
                            className="w-full bg-foreground text-background hover:bg-foreground/90"
                            onClick={() => {
                              try { localStorage.setItem("qw_onboarding_manual_intent", "1"); } catch {}
                              window.location.href = "/login";
                            }}
                          >
                            <LogIn className="mr-1.5 h-3.5 w-3.5" />
                            Continue to sign in
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs text-muted-foreground"
                            onClick={() => setShowLoginPrompt(false)}
                          >
                            Back
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-muted-foreground"
                          onClick={() => {
                            if (!isLoggedIn) {
                              setShowLoginPrompt(true);
                              return;
                            }
                            setShowManualModal(true);
                          }}
                        >
                          <Pencil className="mr-1.5 h-3.5 w-3.5" />
                          Add positions manually
                        </Button>
                      )}

                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-muted-foreground"
                        onClick={handleConnectLater}
                      >
                        Connect later &amp; continue testing
                      </Button>
                      {isLoggedIn && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-muted-foreground"
                          onClick={() => { setShowBrokerConnect(false); setShowPricing(true); }}
                        >
                          I understand the product &mdash; show me pricing
                        </Button>
                      )}
                    </div>
                  </>
                )}

                {connectState === "connecting" && (
                  <div className="text-center py-6 space-y-4">
                    <Loader2 className="h-10 w-10 mx-auto animate-spin text-amber-500" />
                    <div>
                      <p className="font-semibold">Connecting to {BROKERS.find(b => b.value === selectedBroker)?.label}...</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Complete the login in the popup window.
                      </p>
                    </div>
                  </div>
                )}

                {connectState === "processing" && (
                  <div className="text-center py-6 space-y-4">
                    <Loader2 className="h-10 w-10 mx-auto animate-spin text-amber-500" />
                    <div>
                      <p className="font-semibold">Processing your portfolio...</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {processingHint || <>This can take a few minutes. We&apos;re syncing your positions.</>}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={handleContinueToTrial}>
                      Continue while processing
                    </Button>
                  </div>
                )}

                {connectState === "done" && (
                  <div className="text-center py-4 space-y-5">
                    <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-emerald-500" />
                    </div>

                    <div>
                      <p className="font-semibold text-lg">Portfolio connected!</p>
                      {estimatedIncome !== null && estimatedIncome > 0 ? (
                        <div className="mt-3 rounded-xl bg-gradient-to-r from-emerald-950/85 via-emerald-900/65 to-emerald-950/85 border border-emerald-500/25 p-4">
                          <p className="text-xs text-emerald-300/80 uppercase tracking-wider font-semibold">Estimated additional income</p>
                          <p className="text-2xl font-semibold text-emerald-200 tabular-nums mt-1">
                            ${estimatedIncome.toLocaleString()}<span className="text-sm font-normal text-emerald-300/70">/mo</span>
                          </p>
                          <p className="text-[11px] text-zinc-300/70 mt-1">from selling CSPs on your idle cash</p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground mt-1">
                          Your positions are syncing. Start your trial to see personalized results.
                        </p>
                      )}
                    </div>

                    <Button
                      size="lg"
                      className="w-full h-11 bg-gradient-to-r from-lime-400 to-green-400 text-zinc-900 hover:from-lime-500 hover:to-green-500 font-semibold"
                      onClick={handleContinueToTrial}
                    >
                      Start 7 Days for $1
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      <ManualPositionModal
        open={showManualModal}
        onOpenChange={setShowManualModal}
        onComplete={handleManualComplete}
      />

      <IBKRSetupModal
        open={showIBKRModal}
        onOpenChange={setShowIBKRModal}
        onSuccess={async () => {
          setEstimatedIncome(null);
          setConnectState("done");
          try {
            const [connRes, posRes] = await Promise.all([
              fetch("/api/broker/connections"),
              fetch("/api/positions/snaptrade"),
            ]);
            const connData = connRes.ok ? await connRes.json() : null;
            const posData = posRes.ok ? await posRes.json() : null;
            const brokerName =
              connData?.ibkrConnections?.[0]?.accountName ||
              connData?.ibkrConnections?.[0]?.accountId ||
              "IBKR";
            const accounts = posData?.accounts || [];
            const totalCash = accounts.reduce((s: number, a: any) => s + (a.cash || 0), 0);
            setConnectedBrokerInfo({ name: brokerName, cash: totalCash });
            if (totalCash > 0) {
              setEstimatedIncome(Math.round(totalCash * 0.03));
            }
          } catch {
            setConnectedBrokerInfo({ name: "IBKR", cash: 0 });
          }
        }}
      />
    </Dialog>
  );
}
